import * as Actions from '../actions/file-list.actions';
import { compare, compareWebPath } from '../shared/func';
import { ErrCode, EncFile } from '../shared/models';
import { FileItem } from '../shared/models/files.model';

export interface State {
    cwd: FileItem;
    hist_id: number;
    parents: EncFile[];
    pathlist: FileItem[];
    filteredPathlist: FileItem[];
    sync_id: number;
    error: ErrCode;
    progress: number;
    loaded: boolean;
    sorted: boolean;
    showdeleted: boolean;
    deletedAmt: number;
    predicate: string;
    direction: 'asc' | 'desc' | '';
    is_vault: boolean;
    total_favorite: number;
    filterString: string;
}

export const initialState: State = {
    cwd: undefined,
    hist_id: 0,
    parents: [],
    pathlist: [],
    filteredPathlist: [],
    sync_id: 0,
    error: undefined,
    progress: 0,
    loaded: false,
    sorted: false,
    showdeleted: false,
    deletedAmt: 0,
    predicate: 'default',
    direction: '',
    is_vault: false,
    total_favorite: 0,
    filterString: ''
};


export function reducer(state = initialState, action: Actions.All) {
    switch (action.type) {
        case Actions.FILE_LIST_RESET:
            return initialState;
        case Actions.FILE_LIST_SUCCESS:
            return {
                ...state,
                progress: 100,
                loaded: true,
                pathlist: action.payload,
                sorted: false,
                predicate: null,
                direction: null
            };
        case Actions.FILE_LIST_ERROR:
            console.error('An error occurred');
            return Object.assign({}, state, { error: action.payload });
        case Actions.FILE_LIST_SET_CWD:
            return Object.assign({}, state, { cwd: action.payload });
        case Actions.FILE_LIST_SET_PARENTS:
            return Object.assign({}, state, { parents: action.payload });

        case Actions.FILE_LIST_SET_PROGRESS:
            return Object.assign({}, state, { progress: action.payload });
        case Actions.FILE_LIST_SET_HIST_ID:
            return Object.assign({}, state, { hist_id: action.payload });
        case Actions.FILE_LIST_UPDATE:
            return Object.assign({}, state, action.payload);
        case Actions.FILE_LIST_REFRESH:
            return Object.assign({}, state, {
                loaded: false,
                sorted: false,
                hist_id: action.payload.hist_id,
                sync_id: action.payload.sync_id,
                showdeleted: (action.payload.showdeleted > 0),
                is_vault: action.payload.is_vault
            });
        case Actions.FILE_LIST_RELOAD:
            return Object.assign({}, state, {
                hist_id: 0,
                loaded: false,
                sorted: false,
            });
        case Actions.FILE_LIST_SHOW_DELETED:
            return Object.assign({}, state, {
                showdeleted: !state.showdeleted,
                loaded: false,
                sorted: false
            });

        case Actions.FILE_LIST_SELECT:
            const orig = state.pathlist.slice(0);
            for (let i = 0, len = orig.length; i < len; i++) {
                if (orig[i].sync_id == action.payload.sync_id) {
                    orig[i].active = true;
                }
            }
            return Object.assign({}, state, {
                pathlist: orig
            });
        case Actions.FILE_LIST_SELECT_TOGGLE:
            const origToggle = state.pathlist.slice(0);
            for (let i = 0, len = origToggle.length; i < len; i++) {
                if (origToggle[i].sync_id == action.payload.sync_id) {
                    origToggle[i].active = !origToggle[i].active;
                }
            }
            return Object.assign({}, state, {
                pathlist: origToggle
            });
        case Actions.FILE_LIST_SELECT_ALL:
            const origAll = state.pathlist.slice(0);
            for (let i = 0, len = origAll.length; i < len; i++) {
                if (action.payload.active && action.payload.filteredIds && action.payload.filteredIds.length) {
                    if (action.payload.filteredIds.indexOf(origAll[i].id) > -1) {
                        origAll[i].active = true;
                    } else {
                        origAll[i].active = false;
                    }
                } else {
                    origAll[i].active = action.payload.active;
                }
            }
            return Object.assign({}, state, {
                pathlist: origAll
            });
        case Actions.FILE_LIST_SELECT_TYPE:
            const origType = state.pathlist.slice(0);
            const key = action.payload.key;
            const value = action.payload.value;
            for (let i = 0, len = origType.length; i < len; i++) {
                if (action.payload.filteredIds && action.payload.filteredIds.length) {
                    if (action.payload.filteredIds.indexOf(origType[i].id) > -1 && origType[i][key] == value) {
                        origType[i].active = true;
                    } else {
                        origType[i].active = false;
                    }
                } else {
                    if (origType[i][key] == value) {
                        origType[i].active = true;
                    }
                }
            }
            return Object.assign({}, state, {
                pathlist: origType
            });
        case Actions.FILE_LIST_SORT:
            const isAsc = action.payload.direction === 'asc';
            const newlist = state.pathlist.slice(0);
            if (action.payload.direction === '') {
                newlist.sort((a, b) => compareWebPath(a, b, isAsc, true));
            } else {
                switch (action.payload.active) {
                    case 'name':
                        newlist.sort((a, b) => compareWebPath(a, b, isAsc, true));
                        break;
                    case 'usertime':
                        newlist.sort((a, b) => {
                            if (a.usertime === b.usertime) {
                                return compareWebPath(a, b, !isAsc, true);
                            }
                            else if (a.is_dir) {
                                return -1;
                            }
                            else if (b.is_dir) {
                                return 1;
                            }
                            else {
                                return compare(a.usertime, b.usertime, isAsc);
                            }
                        });
                        break;
                    case 'size':
                        newlist.sort((a, b) => {
                            if (a.size === b.size) {
                                return compareWebPath(a, b, !isAsc, true);
                            }
                            else if (a.is_dir) {
                                return -1;
                            }
                            else if (b.is_dir) {
                                return 1;
                            }
                            else {
                                return compare(a.size, b.size, isAsc);
                            }
                        });

                        break;
                    case 'default':
                        newlist.sort((a, b) => compareWebPath(a, b, isAsc, true));
                        break;
                    default:
                        newlist.sort((a, b) => compareWebPath(a, b, isAsc, true));
                        break;
                }
            }
            return Object.assign({}, state, {
                pathlist: newlist,
                sorted: true,
                predicate: action.payload.active,
                direction: action.payload.direction
            });
        case Actions.FILE_LIST_SET_PATHLIST:
            return Object.assign({}, state, {
                sorted: false,
                predicate: null,
                direction: null
            });
        case Actions.FILE_LIST_TOTAL_FAVORITE:
            return Object.assign({}, state, {
                total_favorite: action.payload,
            });
        case Actions.FILE_LIST_SET_THUMBNAILS:
            const thumbnailMap = new Map(action.payload.thumbnails.map(t => [t.cachekey, t]));
            const updatedPathlist = state.pathlist.map(file => {
                const thumbnail = thumbnailMap.get(file.cachekey);
                if (thumbnail?.thumbData) {
                    return {
                        ...file,
                        thumbData: { ...thumbnail.thumbData },
                        thumbsrc: thumbnail.thumbsrc,
                        imgsrc: thumbnail.imgsrc
                    };
                }
                return file;
            });
            return {
                ...state,
                pathlist: updatedPathlist
            };
        case Actions.FILE_LIST_FILTER:
            const filterString = action.payload.searchValue ? action.payload.searchValue.toLowerCase() : '';
            const filteredPathlist = filterString ? state.pathlist.filter((item: FileItem) =>
                item.name.toLowerCase().includes(filterString) ||
                item.date_medium.toLowerCase().includes(filterString) ||
                item.filesize.toLowerCase().includes(filterString)
            ) : [];
            return {
                ...state,
                filteredPathlist,
                filterString: action.payload.searchValue
            };
        case Actions.FILE_LIST_UPDATE_STARRED:
            const starUpdatedPathlist = state.pathlist.map(fileItem => {
                if (action.payload.hasOwnProperty(fileItem.sync_id)) {
                    fileItem = {...fileItem, is_favorite: action.payload[fileItem.sync_id]};
                }
                return fileItem;
            })
            return {
                ...state,
                pathlist: starUpdatedPathlist
            };
        case Actions.FILE_LIST_INSERT_COPY_PLACEHOLDERS:
            let pathlist = state.pathlist.slice(0);
            function generateCopyName(file: FileItem): [string, number] {
                let newName: string;

                let prevIndex = pathlist.indexOf(file);
                while (1) {
                    const name = file.name;
                    const dotIndex = name.lastIndexOf('.');
                    if (dotIndex === -1) {
                        newName = `${name} copy`;
                    }
                    else {
                        const baseName = name.substring(0, dotIndex);
                        const extension = name.substring(dotIndex);
                        newName = `${baseName} copy${extension}`;  
                    }

                    const index = pathlist.findIndex(i => i.name === newName);
                    if (index === -1) {
                        return [newName, prevIndex];
                    }
                    else {
                        prevIndex = index;
                        file = pathlist[index];
                    }
                }
            }

            for (const id of action.payload.fileIds) {
                const orgItem = pathlist.find(i => i.sync_id === id);
                if (orgItem) {
                    const [name, index] = generateCopyName(orgItem);
                    const item = {
                        ...orgItem,
                        id: `DUP-${orgItem.id}`,
                        inactive: true,
                        name,
                    };
                    pathlist.splice(index + (!state.direction || state.direction === 'desc' ? 0 : 1), 0, item);
                }
            }

            return {
                ...state,
                pathlist,
            }
        case Actions.FILE_LIST_DELETE_COPY_PLACEHOLDERS:
            return {
                ...state,
                pathlist: state.pathlist.filter(i => !i.id.startsWith('DUP-')),
            }
        default:
            return state;
    }
}


export const getCwd = (state: State) => state.cwd;
// export const getParents = (state: State) => state.parents;
// export const getAuthenticated = (state: State) => state.authenticated;

// export const getUser = (state: State) => state.user;

// export const getLoginProgress = (state: State) => state.progress;
// export const getErrCode = (state: State) => state.error;
